/**
 * RewardChoreographer.ts — orchestrates the two "moments":
 *
 *  1) color-complete: an ALWAYS-rewarding stardust burst + a warm chime + a
 *     "Color complete! +stardust" banner. Stays cozy regardless of size.
 *
 *  2) picture-complete: a TIERED cinematic. Tier is derived from the effective
 *     multiple of total stake:  nice / Big / Mega / Legendary. Confetti, light
 *     rays, screen shake and audio swell all scale with the tier ONLY.
 */
import { Container, Graphics } from 'pixi.js';
import { makeText, popIn } from '../ui/widgets';
import { Tweens } from '../util/tween';
import { easeOutCubic, easeOutQuart, popSettle } from '../util/ease';
import theme, { hex } from '../render/theme';
import { money, mult as fmtMult } from '../util/format';
import { Juice, ParticleBurst } from './Juice';
import { Audio } from '../util/audio';

export interface RewardContext {
  /** layer that sits above the painting but below the HUD. */
  fxLayer: Container;
  /** full-screen overlay layer for cinematics. */
  overlay: Container;
  viewportW: number;
  viewportH: number;
  juice: Juice;
}

export class RewardChoreographer {
  constructor(private ctx: RewardContext) {}

  /**
   * Color-complete stardust — always rewarding, but visibly TIERED by bonusMult so a x5 win
   * doesn't read identical to a x0.2 floor pay. Closes the feel-review gap "color-complete payoff
   * capped so low that a big bonus feels identical to a tiny one".
   *
   *   tier  | bonusMult range  | label             | particles  | banner colour
   *   none  |   < 0.5          | "Stardust"        |  12        | teal (calm floor)
   *   nice  |   0.5 .. 1.5     | "Color complete"  |  22        | teal
   *   big   |   1.5 .. 3       | "BIG color win"   |  38        | gold
   *   mega  |   ≥ 3            | "MEGA color win"  |  58        | coral + extra shake
   */
  colorComplete(x: number, y: number, bonusAmount: number, bonusMult: number, accent: number): void {
    const tier =
      bonusMult >= 3 ? 'mega' :
      bonusMult >= 1.5 ? 'big' :
      bonusMult >= 0.5 ? 'nice' : 'none';
    const tierIntensity: Record<string, number> = { none: 0.18, nice: 0.4, big: 0.7, mega: 1 };
    const tierLabel: Record<string, string> = { none: 'Stardust', nice: 'Color complete', big: 'BIG color win', mega: 'MEGA color win' };
    const tierColour: Record<string, number> = { none: theme.color.teal, nice: theme.color.teal, big: theme.color.gold2, mega: theme.color.coral };
    const intensity = tierIntensity[tier];

    Audio.colorComplete();
    if (tier === 'mega') Audio.sparkle(); // upper-octave shimmer on top of the arpeggio
    Juice.haptic(0.25 + intensity * 0.4);
    this.ctx.juice.shake(0.08 + intensity * 0.32);

    const particleCount = tier === 'mega' ? 58 : tier === 'big' ? 38 : tier === 'nice' ? 22 : 12;
    ParticleBurst.stardust(
      this.ctx.fxLayer, x, y, particleCount,
      tier === 'mega'
        ? [hex(theme.palette.gold1), hex(theme.palette.coral), accent, hex(theme.palette.purple)]
        : [hex(theme.palette.gold1), hex(theme.palette.tealBright), accent, hex(theme.palette.purple)],
      80 + intensity * 90,
    );
    Juice.glowPing(this.ctx.fxLayer, x, y, hex(theme.palette.gold1), 40 + intensity * 32);
    if (tier === 'mega') {
      // secondary delayed glow + side-bursts so a mega visibly "expands"
      Tweens.delay(140, () => Juice.glowPing(this.ctx.fxLayer, x, y, hex(theme.palette.coral), 80));
      Tweens.delay(280, () => ParticleBurst.stardust(this.ctx.fxLayer, x, y, 22, [hex(theme.palette.gold1), accent], 150));
    }

    // banner — colour + size scale with tier
    const banner = new Container();
    const labelSize = tier === 'mega' ? 22 : tier === 'big' ? 21 : 20;
    const label = makeText(tierLabel[tier], { size: labelSize, weight: '800', color: theme.color.white });
    const amt = makeText('+' + money(bonusAmount), { size: 16, weight: '800', color: hex(theme.palette.gold1) });
    label.anchor.set(0.5); amt.anchor.set(0.5);
    label.position.set(0, -10); amt.position.set(0, 14);
    const w = Math.max(label.width, amt.width) + 52;
    const bg = new Graphics();
    bg.roundRect(-w / 2, -28, w, 56, 28).fill({ color: tierColour[tier], alpha: 0.96 });
    bg.roundRect(-w / 2, -28, w, 56, 28).stroke({ width: 2, color: theme.color.white, alpha: 0.45 });
    banner.addChild(bg, label, amt);
    banner.position.set(x, y - 90);
    this.ctx.fxLayer.addChild(banner);
    popIn(banner, 0, 0.45);
    Tweens.to({
      from: 0, to: 1, duration: tier === 'mega' ? 1900 : 1500,
      onUpdate: (t) => {
        banner.position.y = y - 90 - t * (24 + intensity * 16);
        if (t > 0.7) banner.alpha = 1 - (t - 0.7) / 0.3;
      },
      onComplete: () => banner.destroy({ children: true }),
    });
  }

  /**
   * Picture-complete cinematic. The finished ARTWORK is the star: the ink line-art fades, a soft
   * bloom + a traveling shimmer sweep across the picture, and the win is celebrated in a card at the
   * bottom — so you admire what you made instead of staring at a dimmed slot screen. Tiered FX.
   */
  pictureComplete(
    amount: number,
    effMult: number,
    tier: 'none' | 'nice' | 'big' | 'mega' | 'legendary',
    accentColors: number[],
    opts: { canvasRect: { x: number; y: number; w: number; h: number }; onRevealArt?: () => void; totalStake?: number },
    onDone: () => void,
  ): void {
    const intensityByTier: Record<string, number> = { none: 0.2, nice: 0.4, big: 0.65, mega: 0.85, legendary: 1 };
    const intensity = intensityByTier[tier] ?? 0.4;
    const { viewportW: W, viewportH: H, overlay } = this.ctx;
    const rect = opts.canvasRect;

    // 1) the art comes alive: fade the line-art out, then a soft white bloom over the picture
    opts.onRevealArt?.();
    const bloom = new Graphics();
    overlay.addChild(bloom);
    Tweens.to({
      from: 0, to: 1, duration: 760, ease: easeOutCubic,
      onUpdate: (t) => {
        const a = Math.sin(Math.min(1, t) * Math.PI) * 0.22; // rise then fall
        bloom.clear();
        bloom.roundRect(rect.x, rect.y, rect.w, rect.h, theme.radius.card).fill({ color: theme.color.white, alpha: a });
      },
      onComplete: () => bloom.destroy(),
    });

    // 2) a glossy shimmer band travels across the finished picture
    this.shimmerSweep(rect, 900, 220);

    // 3) a subtle warm glow behind the bottom readout (only really shows for bigger tiers)
    const glow = new Graphics();
    glow.position.set(W / 2, H * 0.8);
    overlay.addChild(glow);
    let glowRot = 0;
    const rayCount = 10 + Math.round(intensity * 14);
    const drawRays = () => {
      glow.clear();
      for (let i = 0; i < rayCount; i++) {
        const a = glowRot + (i / rayCount) * Math.PI * 2;
        const len = Math.max(W, H) * 0.6;
        glow.moveTo(0, 0).lineTo(Math.cos(a) * len, Math.sin(a) * len)
          .lineTo(Math.cos(a + 0.12) * len, Math.sin(a + 0.12) * len).closePath();
        glow.fill({ color: hex(theme.palette.gold1), alpha: 0.05 + intensity * 0.04 });
      }
    };
    drawRays();
    const rayTween = Tweens.to({
      from: 0, to: 1, duration: 5000,
      onUpdate: (t) => { glowRot = t * 0.8; glow.alpha = Math.min(1, t * 6) * (0.3 + intensity * 0.5); drawRays(); },
    });

    // 4) bottom celebration card — keeps the artwork fully visible above it
    const titleByTier: Record<string, string> = {
      none: 'Picture finished', nice: 'Picture Bonus!', big: 'BIG WIN', mega: 'MEGA WIN', legendary: 'LEGENDARY',
    };
    const tierColor: Record<string, number> = {
      none: theme.color.teal, nice: theme.color.teal, big: theme.color.gold2, mega: theme.color.purple, legendary: theme.color.coral,
    };
    const group = new Container();
    group.position.set(W / 2, H * 0.8);
    overlay.addChild(group);

    const cardW = Math.min(W - 48, 420);
    const cardBg = new Graphics();
    cardBg.roundRect(-cardW / 2, -68, cardW, 136, theme.radius.card).fill({ color: theme.color.white, alpha: 0.94 });
    cardBg.roundRect(-cardW / 2, -68, cardW, 136, theme.radius.card).stroke({ width: 2, color: tierColor[tier] ?? theme.color.teal, alpha: 0.7 });

    const title = makeText(titleByTier[tier] ?? 'Picture Bonus!', {
      size: tier === 'legendary' || tier === 'mega' ? 30 : 24, weight: '800',
      color: tierColor[tier] ?? theme.color.teal, align: 'center', letterSpacing: 1,
    });
    title.anchor.set(0.5); title.position.set(0, -40);
    const amt = makeText(money(amount), { size: 44, weight: '800', color: theme.color.ink, align: 'center' });
    amt.anchor.set(0.5); amt.position.set(0, 4);
    const sub = makeText(fmtMult(effMult) + ' total stake', { size: 15, weight: '700', color: theme.color.gold2, align: 'center' });
    sub.anchor.set(0.5); sub.position.set(0, 44);

    // HONEST framing: show net vs total stake when known. Picture bonus alone isn't the round's
    // P/L — the player wagered (sum of color buys) and got back (drips + color-completes +
    // picture-complete). We display picture-bonus prominently, but if a totalStake is provided we
    // also include "after $stake wagered" so net reads at a glance (UKGC anti-LDW guidance).
    let net: ReturnType<typeof makeText> | null = null;
    if (typeof opts.totalStake === 'number' && opts.totalStake > 0) {
      const netVal = amount - opts.totalStake;
      const sign = netVal >= 0 ? '+' : '−';
      net = makeText(`Net  ${sign}${money(Math.abs(netVal))}  after ${money(opts.totalStake)} wagered`, {
        size: 12, weight: '700',
        color: netVal >= 0 ? theme.color.teal : theme.color.coral,
        align: 'center',
      });
      net.anchor.set(0.5); net.position.set(0, 62);
    }

    group.addChild(cardBg, title, amt, sub);
    if (net) group.addChild(net);
    popIn(group, 360, 0.6);
    Tweens.to({
      from: 0, to: amount, duration: 900 + intensity * 700, delay: 520, ease: easeOutQuart,
      onUpdate: (v) => (amt.text = money(v)),
    });

    // FX by tier
    Audio.celebrate(intensity);
    Juice.haptic(intensity);
    this.ctx.juice.shake(0.2 + intensity * 0.6);
    ParticleBurst.stardust(overlay, rect.x + rect.w / 2, rect.y + rect.h / 2, 26 + Math.round(intensity * 34), accentColors, 150 + intensity * 120);
    if (intensity >= 0.4) {
      ParticleBurst.confetti(overlay, W, 40 + Math.round(intensity * 80), [
        hex(theme.palette.gold1), hex(theme.palette.gold2), hex(theme.palette.teal),
        hex(theme.palette.purple), hex(theme.palette.coral), ...accentColors,
      ]);
    }
    if (intensity >= 0.85) {
      Tweens.delay(500, () => {
        ParticleBurst.confetti(overlay, W, 60, [hex(theme.palette.gold1), hex(theme.palette.coral), hex(theme.palette.purple)]);
        this.ctx.juice.shake(0.5);
      });
    }

    // tap-to-continue
    const cont = makeText('Tap to continue', { size: 15, weight: '700', color: theme.color.muted, align: 'center' });
    cont.anchor.set(0.5); cont.position.set(W / 2, H * 0.93); cont.alpha = 0;
    overlay.addChild(cont);
    Tweens.to({ from: 0, to: 1, duration: 400, delay: 1500, onUpdate: (v) => (cont.alpha = v * 0.9) });

    const hit = new Graphics();
    hit.rect(0, 0, W, H).fill({ color: 0, alpha: 0.001 });
    hit.eventMode = 'static'; hit.cursor = 'pointer';
    overlay.addChildAt(hit, 0); // behind the card so the art stays tappable-to-continue too

    let closing = false;
    const close = () => {
      if (closing) return;
      closing = true;
      rayTween.kill();
      Tweens.to({
        from: 1, to: 0, duration: 360, ease: easeOutCubic,
        onUpdate: (v) => { overlay.alpha = v; },
        onComplete: () => {
          overlay.removeChildren().forEach((c) => c.destroy({ children: true }));
          overlay.alpha = 1;
          onDone();
        },
      });
    };
    hit.on('pointertap', close);
    Tweens.delay(7000, close);

    Tweens.to({
      from: 0, to: 1, duration: 2400, delay: 1200, ease: popSettle,
      onUpdate: (t) => { amt.scale.set(1 + Math.sin(t * Math.PI * 3) * 0.03 * intensity); },
    });
  }

  /** A glossy diagonal highlight band that travels left→right across a rect (clipped to it). */
  private shimmerSweep(rect: { x: number; y: number; w: number; h: number }, durationMs: number, delayMs: number): void {
    const { overlay } = this.ctx;
    const mask = new Graphics().roundRect(rect.x, rect.y, rect.w, rect.h, theme.radius.card).fill({ color: 0xffffff });
    const band = new Graphics();
    band.mask = mask;
    overlay.addChild(mask, band);
    const bw = rect.w * 0.28;
    const skew = rect.h * 0.35;
    Tweens.to({
      from: 0, to: 1, duration: durationMs, delay: delayMs, ease: easeOutCubic,
      onUpdate: (t) => {
        if (band.destroyed) return;
        const x = rect.x - bw + t * (rect.w + bw * 2);
        band.clear();
        // soft wide wash + a brighter gold core line for a "glint"
        band.poly([x, rect.y, x + bw, rect.y, x + bw - skew, rect.y + rect.h, x - skew, rect.y + rect.h])
          .fill({ color: theme.color.white, alpha: 0.16 });
        band.poly([x + bw * 0.45, rect.y, x + bw * 0.6, rect.y, x + bw * 0.6 - skew, rect.y + rect.h, x + bw * 0.45 - skew, rect.y + rect.h])
          .fill({ color: hex(theme.palette.gold1), alpha: 0.5 });
      },
      onComplete: () => { if (!band.destroyed) band.destroy(); if (!mask.destroyed) mask.destroy(); },
    });
  }
}
